import { Avatar, Box, Button, Chip, IconButton, List, ListItem, ListItemAvatar, ListItemIcon, ListItemSecondaryAction, ListItemText, ListSubheader, TextField, Typography, useTheme } from "@material-ui/core";
import { ArrowBack, Email, Email as EmailIcon, Link as LinkIcon, Person, PersonAdd } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import copy from 'clipboard-copy';
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Link } from "react-router-dom";

import { Collections } from "Collections";
import { useAppUser } from "components/AppUserProvider";
import { CardButton } from "components/CardButton";
import { FullscreenLoading } from "components/FullscreenLoading";
import { InviteOrgUserWithEmailModal } from "components/InviteOrgUserModal";
import { useOrg } from "components/OrgProvider";
import { useOrgUserEmailInvites } from "components/OrgUserEmailInvitesProvider";
import { useOrgUserInvites } from "components/OrgUserInvitesProvider";
import { useOrgUser } from "components/OrgUserProvider";
import { PageNavigation } from "components/PageNavigation";
import { FirebaseUtils } from "firebaseUtils/FirebaseUtils";
import { withdrawEmailInvite } from "modelActions/OrgUserEmailInvite";
import { withdrawInvite } from "modelActions/OrgUserInvite";
import { AnalyticsEvent } from "models/AnalyticsEvent";
import { AppUser, noAppUserNameText } from "models/AppUser";
import { noEmailText } from "models/Email";
import { DbDoc, WithRef } from "models/FbDoc";
import { Grant } from "models/Grant";
import { Nullable } from "models/Nullable";
import { noOrgNameText } from "models/Org";
import { OrgRoles } from "models/OrgRoles";
import { getOrgUserGrants, noOrgUserNameText, OrgUser } from "models/OrgUser";
import { OrgUserEmailInvite } from "models/OrgUserEmailInvite";
import { OrgUserInvite } from "models/OrgUserInvite";
import { EnumPresentMap } from "models/PropertyMap";
import { Paths } from "Paths";

import { WarningModal } from "./OrgUserScreen";

function getRoleNameString(orgRoles?: EnumPresentMap<OrgRoles.OrgRoles>) {
    const roles: OrgRoles.OrgRole[] = Object.keys(orgRoles || {}) as OrgRoles.OrgRole[]
    const roleNames = roles.map(role => OrgRoles.Flat[role].name);
    return roleNames.join(', ')
}

type OrgUserInviteListProps = {
    invites?: WithRef<DbDoc<OrgUserInvite>>[] | null
    emailInvites?: WithRef<DbDoc<OrgUserEmailInvite>>[] | null
    orgUser?: Nullable<OrgUser>
    appUser?: Nullable<AppUser>
}

export function OrgUserInviteList({ invites, emailInvites, orgUser, appUser }: OrgUserInviteListProps) {
    const { enqueueSnackbar } = useSnackbar()
    const grants = getOrgUserGrants(orgUser || null)
    const canDelete = !!grants[Grant.INVITE_USER]
    const numInvites = (invites?.length || 0) + (emailInvites?.length || 0)
    return (
        <>
            {(numInvites < 1) ? null : (
                <ListSubheader>Inviterte brukere</ListSubheader>
            )}
            {emailInvites?.map(invite => {
                return (
                    <ListItem key={invite.data.id}>
                        <ListItemAvatar>
                            <Avatar>
                                <Email />
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={noEmailText(invite.data.invitedUser.appUserEmail.email)}
                            secondary={getRoleNameString(invite.data.invite.allottedRoles.map)}
                        />
                        <ListItemSecondaryAction>
                            <Chip
                                label="Venter på svar"
                                color="secondary"
                                onDelete={(canDelete && appUser && orgUser) ? () => {
                                    if (!orgUser) {
                                        console.error('No orgUser state!')
                                        return
                                    }
                                    withdrawEmailInvite(orgUser, appUser, invite.data, enqueueSnackbar)
                                } : undefined}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                )

            })}
            {invites?.map(invite => {
                return (
                    <ListItem key={invite.data.id} button component={Link} to={`${Paths.APP_USERS}/${invite.data.invitedUser.appUser.id}`}>
                        <ListItemAvatar>
                            <Avatar src={invite.data.invitedUser.appUser.avatarImage?.original.url || undefined}>
                                <Person />
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={noAppUserNameText(invite?.data.invitedUser.appUser.contactInfo?.name?.fullName)}
                            secondary={getRoleNameString(invite.data.allottedRoles.map)}
                        />
                        <ListItemSecondaryAction>
                            <Chip
                                label="Venter på svar"
                                color="secondary"
                                onDelete={(canDelete && appUser && orgUser) ? () => {
                                    if (!orgUser) {
                                        console.error('No orgUser state!')
                                        return
                                    }
                                    withdrawInvite(orgUser, appUser, invite.data, enqueueSnackbar)
                                } : undefined}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                )
            })}
        </>
    )
}

type OrgUserInviteModalProps = {
    pageNavMode?: boolean
}

export function OrgUserInviteModal({ pageNavMode }: OrgUserInviteModalProps) {
    const theme = useTheme()
    const [appUser] = useAppUser()
    const [orgUser] = useOrgUser()
    const [org] = useOrg()
    const { enqueueSnackbar } = useSnackbar()
    const [mode, setMode] = useState<null | 'select-mode' | 'link' | 'email'>(null)
    const grants = getOrgUserGrants(orgUser?.data || null)

    const orgUrl = org ? (window.location.origin + Paths.ORGS + '/' + org.data.id) : null

    const shareData: Nullable<ShareData> = orgUrl ? {
        title: noOrgUserNameText(appUser?.data.contactInfo?.name?.fullName || appUser?.data.contactInfo?.email?.email) + ' inviterte deg til ' + noOrgNameText(org?.data.name?.text),
        text: 'Følg linken for å be om en invitasjon til ' + noOrgNameText(org?.data.name?.text) + ': ' + orgUrl,
    } : null

    if (!grants[Grant.INVITE_USER]) {
        return null
    }

    return (
        <>
            {(mode && org) ? (
                mode === 'select-mode' ? (
                    <WarningModal
                        title="Send invitasjon"
                        okButtonText="Avbryt"
                        onClose={() => setMode(null)}
                        onCancel={() => setMode(null)}
                        noAutoFocus
                        noCancel
                    >
                        <Typography variant="subtitle2" gutterBottom>
                            Vil du sende invitasjonen som link eller med E-post gjennom TrygKalk?
                        </Typography>
                        <Box my={1} />

                        <Box display="flex" alignItems="center" flexDirection="row" justifyContent="space-evenly" gridGap={theme.spacing(1)}>
                            <CardButton label="Link" icon={<LinkIcon />} disabled={!orgUrl} onClick={() => setMode('link')} />
                            <CardButton label="E-post" icon={<EmailIcon />} disabled={!org || !orgUser || !appUser} onClick={() => setMode('email')} />
                        </Box>
                    </WarningModal>
                ) : (

                    mode === 'link' && !!orgUrl ? (
                        <WarningModal
                            title="Send invitasjon"
                            titlePrefixSlot={
                                <IconButton onClick={() => setMode('select-mode')} edge="start">
                                    <ArrowBack />
                                </IconButton>
                            }
                            okButtonText="Lukk"
                            onClose={() => setMode(null)}
                            onCancel={() => setMode(null)}
                            noAutoFocus
                            noCancel
                        >
                            <Typography variant="subtitle2" gutterBottom>
                                Under finner du en link til Orgen din.
                            </Typography>
                            <Typography variant="subtitle2" gutterBottom>
                                Send linken til dine ansatte slik at de kan be om tilgang til organisasjonen
                            </Typography>
                            <Alert severity="warning" style={{ width: '100%' }}>
                                Ikke del denne linken med noen andre enn organisasjonsmedlemmene dine!
                            </Alert>
                            <Box my={1} />
                            <TextField
                                value={window.location.origin + Paths.ORGS + '/' + org.data.id}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            autoFocus
                                            style={{ marginLeft: theme.spacing(2) }}
                                            onClick={() => copy(orgUrl).then(() => {
                                                enqueueSnackbar('Kopiert!', { variant: 'success' })
                                                setMode(null)
                                            })
                                            }>
                                            Kopier
                                        </Button>
                                    )
                                }}
                            />
                            {shareData && (navigator as any)?.canShare?.(shareData) ? (
                                <>
                                    <Box mt={1} />
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        autoFocus
                                        fullWidth
                                        onClick={() => navigator.share(shareData)}
                                    >
                                        Inviter en ny bruker til {noOrgNameText(org?.data.name?.text)}
                                    </Button>
                                </>
                            ) : null}
                        </WarningModal>
                    ) : (
                        (org && orgUser && appUser) ? (
                            <InviteOrgUserWithEmailModal
                                titlePrefixSlot={
                                    <IconButton onClick={() => setMode('select-mode')} edge="start">
                                        <ArrowBack />
                                    </IconButton>
                                }
                                org={org.data}
                                orgUser={orgUser.data}
                                appUser={appUser.data}
                                onClose={() => setMode(null)}
                                onCancel={() => setMode(null)}
                            />
                        ) : null
                    )
                )
            ) : null
            }
            {
                appUser && org && (
                    <ListItem button onClick={() => setMode('select-mode')}>
                        {pageNavMode ? (
                            <>
                            <ListItemIcon>
                                <PersonAdd color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography color="primary">
                                        Legg til Ansatte
                                    </Typography>
                                }
                            />
                            </>
                        ) : (
                            <>
                                <ListItemIcon>
                                    <PersonAdd />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Inviter ansatte til organisasjonen"
                                    secondary="Trykk her for å legge til flere ansatte i organisasjonen"
                                />
                            </>
                        )}
                    </ListItem>
                )
            }
        </>
    )
}

export function OrgUsersScreen() {
    const [org, loadingOrg] = useOrg()
    const [orgUser, loadingOrgUser] = useOrgUser()
    const [appUser, loadingAppUser] = useAppUser()
    const [orgUsers, loadingOrgUsers] = FirebaseUtils.useCollectionData<OrgUser>(
        true,
        Collections.ORG_USERS,
        AnalyticsEvent.GET_ORG_USERS_ERROR,
        {},
        (query, path) => (
            org && query.where(path.data.org.id, '==', org.data.id
            ))
    )

    const [invites, loadingInvites] = useOrgUserInvites()
    const [emailInvites, loadingEmailInvites] = useOrgUserEmailInvites()

    const loading = loadingInvites || loadingEmailInvites || loadingOrgUsers || loadingOrgUser || loadingOrg || loadingAppUser

    const content = (loading) ? (
        <FullscreenLoading screenId="OrgUsers" />
    ) : (
        <>
            <OrgUserInviteModal />
            <List>
                <OrgUserInviteList appUser={appUser?.data} orgUser={orgUser?.data} invites={invites} emailInvites={emailInvites} />
                {!invites?.length ? null : (
                    <ListSubheader>Organisasjonsmedlemmer</ListSubheader>
                )}
                {orgUsers?.map(({ data: orgUser }) => (
                    <ListItem key={orgUser.id} button component={Link} to={`${Paths.ORG_USERS}/${orgUser.id}`}>
                        <ListItemAvatar>
                            <Avatar src={orgUser.avatarImage?.original.url || undefined}>
                                <Person />
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={noOrgUserNameText(orgUser?.orgContactInfo?.name?.fullName)}
                            secondary={getRoleNameString(orgUser.orgRoles.map)}
                        />
                    </ListItem>
                ))}
                {orgUsers?.length ? null : (
                    <ListItem button>
                        <ListItemText
                            primary="Organisasjonen har ingen brukere"
                            secondary="For å invitere en bruker må de sende deg profilen sin"
                        />
                    </ListItem>
                )}
            </List>
        </>
    )

    return (
        <PageNavigation
            title="Org Brukere"
        >
            {content}
        </PageNavigation>
    )
}
